import { Component, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import { CompanyMessagesModel }   from '../companies/models/company.messages';
import { CompanyService }         from '../companies/company.service';
import { ConfirmService }         from '../messages/confirm/confirm.service';
import { MessagesService }        from '../messages/messages.service';
import { Table }                  from '../table/types/table';
import { TableQueryParameters }   from '../table/types/table-query-parameters';
import { TableService }           from '../table/table.service';
import { VendorActionsComponent } from './vendor.actions.component';
import { VendorModel }            from './models/vendor';
import { VendorsFiltersModel }    from './typings/vendors.filters';
import { VendorsService }         from './vendors.service';
import { BindHtmlComponent } from '../table/cell-components/bind-html.component';

@Component({
  selector   : 'wor-vendors',
  template: require('./vendors.component.html')
})
export class VendorsComponent implements OnInit {
  @Input() companyMessages: CompanyMessagesModel = {} as CompanyMessagesModel;
  @Input() vendors        : Array<VendorModel>   = [];

  filters : VendorsFiltersModel = this.tableService.setFiltersFromCache(this.vendorsService.tableCacheKey, {
      archivedOnly: false,
      value       : ''
    });

  tableSettings : Table = {
    broadcast: this.vendorsService.broadcastKey,
    cache    : {
      enabled: true,
      key    : this.vendorsService.tableCacheKey
    },
    columns: [
      {
        field : 'company_name',
        header: { text: this.translateService.instant('VENDOR_SPACE.SUPPLIER') }
      },
      {
        field : 'suburb',
        header: { text: this.translateService.instant('GENERAL_SPACE.FIELD.SUBURB') }
      },
      {
        component: {
          bindings: {
            inputs: {
              model: 'phone',
              pipe : 'phone'
            }
          },
          enabled: true,
          factory: BindHtmlComponent
        },
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.PHONE_NUMBER')
        }
      },
      {
        field : 'web',
        header: { text: this.translateService.instant('JS_SPACE.COLUMNS.WEBSITE') }
      },
      {
        component: {
          bindings: {
            outputs: {
              onActivateClick     : ( vendor : VendorModel ) => this.activate(vendor),
              onEditClick         : ( vendor : VendorModel ) => this.viewVendor(vendor),
              onEmailClick        : ( vendor : VendorModel ) => this.sendEmail(vendor),
              onInvoiceClick      : ( vendor : VendorModel ) => this.createInvoice(vendor),
              onPaymentClick      : ( vendor : VendorModel ) => this.createPayment(vendor),
              onPurchaseOrderClick: ( vendor : VendorModel ) => this.createPurchaseOrder(vendor),
              onSMSClick          : ( vendor : VendorModel ) => this.sendSMS(vendor)
            }
          },
          enabled: true,
          factory: VendorActionsComponent,
        },
        sortable: false,
      }
    ],
    empty: {
      text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_SUPPLIERS_FOUND')
    },
    pagination: {
      enabled   : true,
      pageSize  : this.tableService.getCachedPaging(this.vendorsService.tableCacheKey).pageSize || 10,
      pageSizes : [ 10, 25, 50 ],
      queryCount: () => this.vendorsService.getCount(this.filters)
    },
    rows: {
      highlight: {
        enabled: true
      }
    },
    sorting: this.tableService.setSortingFromCache(this.vendorsService.tableCacheKey, {
      direction: 'asc',
      field    : 'company_name'
    })
  };

  constructor (
    public  companyService   : CompanyService,
    private confirmService   : ConfirmService,
    private messagesService  : MessagesService,
    private router           : UIRouter,
    private tableService     : TableService,
    private translateService : TranslateService,
    private vendorsService   : VendorsService
  ) {}

  ngOnInit () : void {
    if ( !this.companyMessages ) {
      this.vendorsService.getDefaultMessages()
      .then(( companyMessages : CompanyMessagesModel ) => {
        this.companyMessages = companyMessages;
      });
    }
  }

  activate ( vendor : VendorModel ) : void {
    this.confirmService.generic(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATE', {
      name: vendor.company_name
    }))
    .then(() => this.vendorsService.reactivate(vendor))
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SUPPLIER_REACTIVATED'), 'success');

      this.router.stateService.reload();
    })
    .catch(error => this.messagesService.error(error));
  }

  applyFilters () : void {
    if ( window[ this.vendorsService.broadcastKey ] ) {
      window[ this.vendorsService.broadcastKey ](this.filters);
    }
  }

  clearFilter () : void {
    this.filters.value = '';

    this.applyFilters();
  }

  createInvoice ( vendor : VendorModel ) : void {
    this.router.stateService.go('app.vendor-invoice', {
      vendor_id: vendor.id
    });
  }

  createPayment ( vendor : VendorModel ) : void {
    this.router.stateService.go(vendor.is_branch_vendor
        ? 'app.vendor-branch-payment'
        : 'app.vendor-payment', {
      vendor_id: vendor.id
    });
  }

  createPurchaseOrder ( vendor : VendorModel ) : void {
    this.router.stateService.go('app.purchase-order', {
      vendor_id: vendor.id
    });
  }

  getVendors ( params : TableQueryParameters ) : void {
    this.vendorsService.get(params, this.filters)
    .then(vendors => {
      this.vendors = vendors;
    });
  }

  sendEmail ( vendor : VendorModel ) : void {
    this.vendorsService.sendEmail(vendor, this.companyMessages.supplier_email_message)
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'))
    .catch(error => this.messagesService.error(error));;
  }

  sendSMS ( vendor : VendorModel ) : void {
    this.vendorsService.sendSms(vendor)
    .then(() => this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.MESSAGE_SENT'), 'success'))
    .catch(error => this.messagesService.error(error));;
  }

  viewVendor ( vendor : VendorModel ) : void {
    this.router.stateService.go('app.vendor', {
      id: vendor.id
    });
  }
}
